import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  formatDate,
  formatDateText,
} from './CommonFunctions';
import Header from './Header';
import {
  Grid,
  Button,
  Box,
  Paper,
  Container,
  Typography,
  Stack,
  Card,
  CardContent,
  TextField,
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import EditDeposit from './CashManagement/EditDeposit';
import { NumericFormat } from 'react-number-format';
import { DataGrid } from '@mui/x-data-grid';

const thisMonth = new Date();
thisMonth.setDate(1);

const DepositReport = (props) => {
  const url = process.env.REACT_APP_SERVER_URL;
  const [depositDetails, setDepositDetails] = useState([]);
  const [searchStartDate, setSearchStartDate] = useState(formatDate(thisMonth));
  const [searchEndDate, setSearchEndDate] = useState(formatDate(new Date(), 1));
  const [showDepositedPayments, setShowDepositedPayments] = useState(false);
  const [currentDeposit, setCurrentDeposit] = useState({});
  const [filteredRows, setFilteredRows] = useState([]);

  useEffect(() => {
    getAllDeposits();
  }, []);

  const filterRowsBySearchDates = (depositDetails, searchStartDate, searchEndDate) => {
    const filtered = depositDetails.filter((row) => {
      if (row.depositDate >= searchStartDate && row.depositDate <= searchEndDate)
        return row;
    });
    console.log(filtered);
    setFilteredRows(filtered);
  }

  const getAllDeposits = async () => {
    try {
      await axios.get(`${url}/cashDeposits`).then((responseForDeposits) => {
        console.log(responseForDeposits.data);
        setDepositDetails(responseForDeposits.data);
        filterRowsBySearchDates(responseForDeposits.data, searchStartDate, searchEndDate);
      });
    } catch (error) {
      console.log(error);
    }
  }

  const searchDepositsByDateRange = async (searchStartDate, searchEndDate) => {
    try {
      console.log("Search Deposits By Date Range. Search Start Date : " + searchStartDate);
    } catch (error) {
      console.log(error);
    }
  }

  const totalDeposits = filteredRows.length;
  const totalAmount = filteredRows.reduce((sum, row) => sum + (row.amount || 0), 0);
  const totalExpected = filteredRows.reduce((sum, row) => sum + (row.expectedAmount || 0), 0);
  const totalDifference = totalAmount - totalExpected;

  const columns = [
    {
      field: 'id',
      headerName: 'Deposit Id',
      width: 100,
      align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return <Link onClick={(e) => {
          setCurrentDeposit(currentRow);
          setShowDepositedPayments(true);
        }}>{currentRow.id}</Link>
      }
    },
    {
      field: 'depositDate',
      headerName: 'Deposit Date',
      width: 180,
      align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return currentRow.depositDate ?
          formatDateText(currentRow.depositDate) : null;
      }
    },
    {
      field: 'depositedBy',
      headerName: 'Deposited By',
      width: 120,
      align: 'left'
    },
    {
      field: 'amount',
      headerName: 'Deposited Amount',
      width: 150,
      align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return <NumericFormat value={currentRow.amount}
          thousandSeparator prefix={'$'} displayType="text" />
      }
    },
    {
      field: 'expectedAmount',
      headerName: 'Expected Amount',
      width: 180,
      align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return <NumericFormat value={currentRow.expectedAmount}
          thousandSeparator prefix={'$'} displayType="text" />
      }
    },
    {
      field: 'diff',
      headerName: 'Difference',
      width: 120,
      align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return <NumericFormat value={currentRow.amount - currentRow.expectedAmount}
          thousandSeparator prefix={'$'} displayType="text" />
      }
    },
    {
      field: 'bankName',
      headerName: 'Bank Details',
      width: 180,
      align: 'left'
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      width: 180,
      align: 'left'
    },
  ];

  return (
    <>
      <Header />
      <Container maxWidth="xl" sx={{ mt: 3, mb: 2 }}>
       
          <Box sx={{ display: 'flex', justifyContent: 'space-between', 
                            alignItems: 'center', mb: 3,
                              pb: 2,borderBottom: 1,borderColor: "divider", }}>
            <Typography variant="h6" fontWeight="600">
              Deposit Report
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={searchStartDate}
                onChange={(e) => setSearchStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 180 }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={searchEndDate}
                onChange={(e) => setSearchEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 180 }}
              />
              <Button
                variant="contained"
                onClick={(e) => filterRowsBySearchDates(depositDetails, searchStartDate, searchEndDate)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Search
              </Button>
            </Stack>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                      <ReceiptIcon />
                    </Box>
                    <Box>
                      <Typography variant="body" color="text.secondary">Total Deposits</Typography>
                      <Typography variant="h6" fontWeight="600">{totalDeposits}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ bgcolor: 'success.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                      <MoneyIcon />
                    </Box>
                    <Box>
                      <Typography variant="body" color="text.secondary">Total Amount</Typography>
                      <Typography variant="h6" fontWeight="600">
                        <NumericFormat value={totalAmount} thousandSeparator prefix={'$'} displayType="text" />
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ bgcolor: 'info.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                      <BankIcon />
                    </Box>
                    <Box>
                      <Typography variant="body" color="text.secondary">Expected Amount</Typography>
                      <Typography variant="h6" fontWeight="600">
                        <NumericFormat value={totalExpected} thousandSeparator prefix={'$'} displayType="text" />
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ bgcolor: totalDifference >= 0 ? 'warning.main' : 'error.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                      <TrendingUpIcon />
                    </Box>
                    <Box>
                      <Typography variant="body" color="text.secondary">Difference</Typography>
                      <Typography variant="h6" fontWeight="600">
                        <NumericFormat value={totalDifference} thousandSeparator prefix={'$'} displayType="text" />
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper elevation={2} sx={{ borderRadius: 2 }}>
          <Box sx={{ height: 500, width: '100%' }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              getRowId={(row) => row.id}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              sx={{
                border: "none",
                "& .MuiDataGrid-cell": {
                  borderBottom: 1,
                  borderColor: "divider",
                },
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: "grey.50",
                  borderBottom: 2,
                  borderColor: "divider",
                },
              }}
            />
          </Box>
        </Paper>
      </Container>

      {showDepositedPayments && <EditDeposit
        currentDeposit={currentDeposit}
        showDepositedPayments={showDepositedPayments}
        setShowDepositedPayments={setShowDepositedPayments}
      />}
    </>
  );
}

export default DepositReport;